import React from "react";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { i18n } from "../../translate/i18n.js";

// feat_289 — Editor semanal con HORARIO PARTIDO (múltiples franjas por día).
// Formato serializado (nuevo, array de franjas por día; día cerrado = []):
//   { monday: [{ open: "09:00", close: "13:00" }, { open: "16:00", close: "20:00" }], ... }
// Lee también el formato legacy single-range { day: { enabled, open, close } }
// (backward-compat) y lo normaliza a array al primer cambio.
//
// Props:
//   value: objeto de horario (nuevo o legacy) o {} vacío
//   onChange(nextValue): callback con el objeto completo actualizado (siempre array)
//   onCommit(nextValue): opcional, al soltar/togglear para persistir

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

const DEFAULT_RANGE = { open: "09:00", close: "18:00" };

// Normaliza el valor de UN día a { enabled, ranges: [{open,close}] }.
const readDay = dayValue => {
  if (Array.isArray(dayValue)) {
    const ranges = dayValue.filter(r => r && r.open && r.close);
    return { enabled: ranges.length > 0, ranges: ranges.length ? ranges : [{ ...DEFAULT_RANGE }] };
  }
  if (dayValue && typeof dayValue === "object") {
    // legacy single-range
    return {
      enabled: !!dayValue.enabled,
      ranges: [
        {
          open: dayValue.open || DEFAULT_RANGE.open,
          close: dayValue.close || DEFAULT_RANGE.close
        }
      ]
    };
  }
  return { enabled: false, ranges: [{ ...DEFAULT_RANGE }] };
};

const WeeklySchedulePicker = ({ value, onChange, onCommit }) => {
  const schedule = value && typeof value === "object" ? value : {};

  // Serializa el estado interno de un día a lo que se guarda (array o []).
  const serializeDay = ({ enabled, ranges }) =>
    enabled ? ranges.map(r => ({ open: r.open, close: r.close })) : [];

  const commitDay = (day, dayState, commit) => {
    const next = { ...schedule, [day]: serializeDay(dayState) };
    if (onChange) onChange(next);
    if (commit && onCommit) onCommit(next);
  };

  const toggleDay = (day, checked) => {
    const cur = readDay(schedule[day]);
    commitDay(day, { ...cur, enabled: checked }, true);
  };

  const updateRange = (day, idx, patch, commit) => {
    const cur = readDay(schedule[day]);
    const ranges = cur.ranges.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    commitDay(day, { ...cur, ranges }, commit);
  };

  const addRange = day => {
    const cur = readDay(schedule[day]);
    commitDay(
      day,
      { ...cur, enabled: true, ranges: [...cur.ranges, { ...DEFAULT_RANGE }] },
      true
    );
  };

  const removeRange = (day, idx) => {
    const cur = readDay(schedule[day]);
    const ranges = cur.ranges.filter((_, i) => i !== idx);
    commitDay(
      day,
      {
        ...cur,
        enabled: ranges.length > 0,
        ranges: ranges.length ? ranges : [{ ...DEFAULT_RANGE }]
      },
      true
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {DAYS.map(day => {
        const cur = readDay(schedule[day]);
        return (
          <div
            key={day}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              flexWrap: "wrap"
            }}
          >
            <div
              style={{
                minWidth: 130,
                display: "flex",
                alignItems: "center",
                paddingTop: 6
              }}
            >
              <Switch
                size="small"
                color="primary"
                checked={cur.enabled}
                onChange={e => toggleDay(day, e.target.checked)}
              />
              <Typography variant="body2">
                {i18n.t(`weeklySchedule.days.${day}`)}
              </Typography>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {cur.ranges.map((r, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                >
                  <TextField
                    type="time"
                    size="small"
                    variant="outlined"
                    disabled={!cur.enabled}
                    value={r.open}
                    onChange={e => updateRange(day, idx, { open: e.target.value }, false)}
                    onBlur={() => onCommit && onCommit(schedule)}
                    inputProps={{ step: 300 }}
                    style={{ width: 120 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("weeklySchedule.to")}
                  </Typography>
                  <TextField
                    type="time"
                    size="small"
                    variant="outlined"
                    disabled={!cur.enabled}
                    value={r.close}
                    onChange={e => updateRange(day, idx, { close: e.target.value }, false)}
                    onBlur={() => onCommit && onCommit(schedule)}
                    inputProps={{ step: 300 }}
                    style={{ width: 120 }}
                  />
                  {cur.enabled && cur.ranges.length > 1 && (
                    <IconButton
                      size="small"
                      title={i18n.t("weeklySchedule.removeRange")}
                      onClick={() => removeRange(day, idx)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              ))}
              {cur.enabled && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addRange(day)}
                  style={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  {i18n.t("weeklySchedule.addRange")}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WeeklySchedulePicker;
